import type { ApiEndpointBuilder } from "../apiEndpointHelpers";
import type {
  AdminV2SupportPreviewKeepListAddRequest,
  AdminV2SupportPreviewKeepListAddResponse,
  AdminV2SupportPreviewKeepListCapabilityResponse,
  AdminV2SupportPreviewKeepListListResponse,
  AdminV2SupportPreviewKeepListRestoreRequest,
  AdminV2SupportPreviewKeepListRestoreResponse,
  AdminV2SupportPreviewKeepListSnapshotRequest,
  AdminV2SupportPreviewKeepListSnapshotResponse,
} from "./adminV2SupportPreviewKeepListTypes";

/** Single static tag: the whole list (entries + status) is small and always read together. */
const PREVIEW_KEEP_LIST_LIST_TAG = {
  type: "AdminV2SupportPreviewKeepList",
  id: "LIST",
} as const;

/**
 * RTK Query endpoints for the staging-only "Preview keep-list" support tool.
 *
 * @remarks
 * Add/remove/snapshot/restore all invalidate the single list tag, since every
 * one of them changes either the entries or the status (last snapshot/last
 * restore/pending-restore) shown on the same page. Mirrors
 * `adminV2SupportTestResetEndpoints.ts`.
 */
export const buildAdminV2SupportPreviewKeepListEndpoints = (
  builder: ApiEndpointBuilder,
) => ({
  getAdminV2SupportPreviewKeepListCapability: builder.query<
    AdminV2SupportPreviewKeepListCapabilityResponse,
    void
  >({
    query: () => ({ url: "/admin/v2/support/preview-keep-list/capability" }),
    keepUnusedDataFor: 60,
  }),

  getAdminV2SupportPreviewKeepList: builder.query<
    AdminV2SupportPreviewKeepListListResponse,
    void
  >({
    query: () => ({ url: "/admin/v2/support/preview-keep-list" }),
    providesTags: [PREVIEW_KEEP_LIST_LIST_TAG],
  }),

  addAdminV2SupportPreviewKeepListEntry: builder.mutation<
    AdminV2SupportPreviewKeepListAddResponse,
    AdminV2SupportPreviewKeepListAddRequest
  >({
    query: (body) => ({
      url: "/admin/v2/support/preview-keep-list",
      method: "POST",
      data: body,
    }),
    invalidatesTags: [PREVIEW_KEEP_LIST_LIST_TAG],
  }),

  removeAdminV2SupportPreviewKeepListEntry: builder.mutation<
    void,
    { organisationId: string }
  >({
    query: ({ organisationId }) => ({
      url: `/admin/v2/support/preview-keep-list/${encodeURIComponent(organisationId)}`,
      method: "DELETE",
    }),
    invalidatesTags: [PREVIEW_KEEP_LIST_LIST_TAG],
  }),

  snapshotAdminV2SupportPreviewKeepList: builder.mutation<
    AdminV2SupportPreviewKeepListSnapshotResponse,
    AdminV2SupportPreviewKeepListSnapshotRequest | void
  >({
    query: (body) => ({
      url: "/admin/v2/support/preview-keep-list/snapshot",
      method: "POST",
      data: body ?? {},
    }),
    invalidatesTags: [PREVIEW_KEEP_LIST_LIST_TAG],
  }),

  restoreAdminV2SupportPreviewKeepList: builder.mutation<
    AdminV2SupportPreviewKeepListRestoreResponse,
    AdminV2SupportPreviewKeepListRestoreRequest | void
  >({
    query: (body) => ({
      url: "/admin/v2/support/preview-keep-list/restore",
      method: "POST",
      data: body ?? {},
    }),
    invalidatesTags: [PREVIEW_KEEP_LIST_LIST_TAG],
  }),
});
